import React, { Fragment } from "react";
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Skeleton,
} from "@mui/material";
import { BarChart } from "@mui/x-charts";
import { fillParent, footerFlex, scrollingBox } from "utils/base-styles";
import PageTitle from "components/layout/PageTitle";
import Footer from "components/layout/Footer";
import { useComparisonAnalytics } from "features/PublisherAnalytics/hooks/comparison-analytics.hook";
import {
  COMPARISON_TIMEFRAME_OPTIONS,
  DEMOGRAPHIC_OPTIONS,
  REGION_OPTIONS,
  TOPIC_OPTIONS,
  METRIC_OPTIONS,
} from "features/PublisherAnalytics/models/comparison.model";
import PublisherComparisonTable from "features/PublisherAnalytics/components/PublisherComparisonTable";
import EngagementLeaderboard from "features/PublisherAnalytics/components/EngagementLeaderboard";
import ShareComparison from "features/PublisherAnalytics/components/ShareComparison";
import TimeFrameSelect from "features/PublisherAnalytics/components/TimeFrameSelect";
import MultiSelect from "components/ui/MultiSelect";
import SoothCard from "components/base/SoothCard";
import PublisherSearchShareChart from "features/PublisherAnalytics/components/PublisherSearchShareChart";
import PublisherSelect from "features/PublisherAnalytics/components/PublisherSelect";
import { Helmet } from "react-helmet-async";

const Comparision = () => {
  const {
    timeframe,
    demographic,
    region,
    topic,
    metric,
    category,
    setTimeframe,
    setDemographic,
    setRegion,
    setTopic,
    setMetric,
    setCategory,
    comparisonByTopic,
    engagementLeaderboard,
    visitorShare,
    bookmarksShare,
    isLoadingComparisonByTopic,
    isLoadingEngagementLeaderboard,
    isLoadingVisitorShare,
    isLoadingBookmarksShare,
    isLoadingPublisherShare,
    publisherId,
    setPublisherId,
    roles,
    publisherShare,
  } = useComparisonAnalytics();

  const selectedTopic = TOPIC_OPTIONS.find((t) => t.topicId === topic);
  const selectedMetric = METRIC_OPTIONS.find((m) => m.metricId === metric);

  const demographicOptions = DEMOGRAPHIC_OPTIONS.map((option) => ({
    value: option.gradeLevelKey,
    label: option.gradeLevelValue,
  }));

  const regionOptions = REGION_OPTIONS.map((option) => ({
    value: option.stateCode,
    label: option.stateName,
  }));

  return (
    <Fragment>
      <Helmet>
        <title>Sooth - Publishers - Comparison</title>
      </Helmet>
      <Box
        sx={{
          "@media screen": {
            ...fillParent,
            ...scrollingBox,
            ...footerFlex,
          },
          "@media print": {
            m: 3,
          },
        }}
      >
        <Box sx={{ display: "grid", gap: 2, mb: 4, p: 2 }}>
          <Stack
            sx={{
              flexDirection: { md: "column", lg: "row" },
            }}
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <PageTitle sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap" }}>
              Publisher Comparison
            </PageTitle>
            <Stack
              direction={{ sm: "column", md: "row" }}
              spacing={2}
              alignItems="center"
            >
              <PublisherSelect
                publisherId={publisherId}
                setPublisherId={setPublisherId}
                roles={roles}
              />
              <TimeFrameSelect
                timeframe={timeframe}
                setTimeframe={setTimeframe}
                label="Timeframe"
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gap: 3,

              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "1fr",
                lg: "45% 55%",
              },
              gridTemplateRows: {
                xs: "auto auto auto",
                lg: "auto 1fr",
              },

              gridTemplateAreas: {
                xs: `"table"
                    "leaderboard"
                    "share"`,
                lg: `"table leaderboard"
                    "table share"`,
              },
            }}
          >
            <Box sx={{ gridArea: "table" }}>
              <PublisherComparisonTable
                data={comparisonByTopic}
                isLoading={isLoadingComparisonByTopic}
                topic={selectedTopic?.label}
                topicValue={topic}
                setTopic={setTopic}
                category={category}
                setCategory={setCategory}
              />
              <Box sx={{ mt: 2 }}>
                {/* <PublisherSearchShareChart 
                   data={publisherShare} 
                   isLoading={isLoadingPublisherShare} 
                 /> */}
              </Box>
            </Box>

            <Box sx={{ gridArea: "leaderboard" }}>
              <EngagementLeaderboard
                data={engagementLeaderboard}
                isLoading={isLoadingEngagementLeaderboard}
                metric={selectedMetric?.label}
                metricValue={metric}
                setMetric={setMetric}
              />
            </Box>

            <Box sx={{ gridArea: "share" }}>
              <ShareComparison
                visitorData={visitorShare}
                bookmarksData={bookmarksShare}
                isLoadingVisitor={isLoadingVisitorShare}
                isLoadingBookmarks={isLoadingBookmarksShare}
              />
            </Box>
          </Box>
        </Box>
        <Footer sx={{ px: 0 }} />
      </Box>
    </Fragment>
  );
};

export default Comparision;
